import React, { useEffect, useState } from 'react';
import { MessageSquare, Clock, Search, ShoppingCart, CreditCard } from 'lucide-react';
import api from '../../../api/axios';

function formatDuration(seconds) {
  if (!seconds || seconds <= 0) return '0s';
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function formatTrend(value) {
  if (value === 0 || value === null || value === undefined) return null;
  const sign = value > 0 ? '+' : '';
  return `${sign}${value}%`;
}

const metricConfig = [
  {
    key: 'totalSessions',
    trendKey: 'totalSessionsTrend',
    title: 'Total Conversations',
    icon: MessageSquare,
    iconColor: 'text-blue-500',
    iconBg: 'bg-blue-50',
    format: (v) => v?.toLocaleString() ?? '—',
  },
  {
    key: 'avgDuration',
    trendKey: 'avgDurationTrend',
    title: 'Avg Chat Duration',
    icon: Clock,
    iconColor: 'text-violet-500',
    iconBg: 'bg-violet-50',
    format: (v) => formatDuration(v),
  },
  {
    key: 'productQueries',
    trendKey: 'productQueriesTrend',
    title: 'Product Queries',
    icon: Search,
    iconColor: 'text-amber-500',
    iconBg: 'bg-amber-50',
    format: (v) => v?.toLocaleString() ?? '—',
  },
  {
    key: 'addToCartAssisted',
    trendKey: 'addToCartAssistedTrend',
    title: 'Add to Cart Assisted',
    icon: ShoppingCart,
    iconColor: 'text-emerald-500',
    iconBg: 'bg-emerald-50',
    format: (v) => v?.toLocaleString() ?? '—',
  },
  {
    key: 'checkoutInitiated',
    trendKey: 'checkoutInitiatedTrend',
    title: 'Checkout Initiated',
    icon: CreditCard,
    iconColor: 'text-rose-500',
    iconBg: 'bg-rose-50',
    format: (v) => v?.toLocaleString() ?? '—',
  },
];

const SAMetricCards = ({ from, to }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!from || !to) return;
    let cancelled = false;
    setLoading(true);
    api
      .get('/api/v1/shopping-assistance/metrics', { params: { from, to } })
      .then((res) => {
        if (!cancelled) setData(res.data?.data);
      })
      .catch(() => {
        if (!cancelled) setData(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [from, to]);

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
      {metricConfig.map((m) => {
        const Icon = m.icon;
        const value = data?.[m.key];
        const trend = data?.[m.trendKey];
        const trendLabel = formatTrend(trend);
        const trendUp = trend > 0;

        return (
          <div
            key={m.key}
            className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex flex-col gap-3 hover:shadow-md transition-shadow"
          >
            <div className="flex items-center justify-between">
              <div className={`w-9 h-9 rounded-lg ${m.iconBg} flex items-center justify-center`}>
                <Icon size={18} className={m.iconColor} />
              </div>
              {trendLabel && (
                <span
                  className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                    trendUp ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-500'
                  }`}
                >
                  {trendLabel}
                </span>
              )}
            </div>
            <div>
              <p className="text-xl md:text-2xl font-bold text-gray-800 leading-tight">
                {loading ? '—' : m.format(value)}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">{m.title}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default SAMetricCards;
